import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  VStack,
  HStack,
  Box,
  Text,
  Icon,
  Input,
  Flex,
  Link,
  Checkbox,
  FormControl,
  FormLabel,
  FormErrorMessage,
  useColorMode,
  useClipboard,
} from "@chakra-ui/react";
import { Link as RouterLink } from "react-router-dom";
import { Clock, Check, Copy } from "lucide-react";

interface SignUpModalProps {
  isOpen: boolean;
  onCancel: () => void;
  onSignUp: () => void;
  onComplete: () => void;
  signUpUsername: string;
  setSignUpUsername: (value: string) => void;
  signUpUsernameError: string;
  setSignUpUsernameError: (value: string) => void;
  isSigningUp: boolean;
  generatedKey: string | null;
  acceptedTerms: boolean;
  setAcceptedTerms: (value: boolean) => void;
  termsError: boolean;
  setTermsError: (value: boolean) => void;
}

export function SignUpModal({
  isOpen,
  onCancel,
  onSignUp,
  onComplete,
  signUpUsername,
  setSignUpUsername,
  signUpUsernameError,
  setSignUpUsernameError,
  isSigningUp,
  generatedKey,
  acceptedTerms,
  setAcceptedTerms,
  termsError,
  setTermsError,
}: SignUpModalProps) {
  const { colorMode } = useColorMode();
  const { hasCopied, onCopy } = useClipboard(generatedKey || "");

  return (
    <Modal
      isOpen={isOpen}
      onClose={onCancel}
      size="lg"
      closeOnOverlayClick={!generatedKey}
      isCentered
    >
      <ModalOverlay backdropFilter="blur(4px)" />
      <ModalContent
        bg={colorMode === "dark" ? "stone.800" : "white"}
        borderRadius="xl"
        overflow="hidden"
      >
        {!generatedKey ? (
          <>
            <ModalHeader
              pb={4}
              pt={6}
              borderBottomWidth="1px"
              borderColor={colorMode === "dark" ? "stone.700" : "stone.100"}
            >
              <VStack spacing={1} align="start">
                <Text fontSize="xl" fontWeight="bold">
                  Create Anonymous Account
                </Text>
                <Text fontSize="sm" fontWeight="normal" color="stone.500">
                  Get started with secure markdown sharing
                </Text>
              </VStack>
            </ModalHeader>
            <ModalCloseButton top={6} />
            <ModalBody pt={6} pb={6}>
              <VStack spacing={5} align="stretch">
                <FormControl isInvalid={!!signUpUsernameError}>
                  <FormLabel fontSize="sm" fontWeight="medium" mb={2}>
                    Account Name
                  </FormLabel>
                  <Input
                    placeholder="e.g., john, design-team, acme-corp"
                    value={signUpUsername}
                    onChange={(e) => {
                      setSignUpUsername(e.target.value);
                      setSignUpUsernameError("");
                    }}
                    onKeyDown={(e) => e.key === "Enter" && onSignUp()}
                    autoFocus
                    size="lg"
                    borderRadius="lg"
                  />
                  {signUpUsernameError ? (
                    <FormErrorMessage>{signUpUsernameError}</FormErrorMessage>
                  ) : (
                    <Text fontSize="xs" color="stone.500" mt={2}>
                      Choose a name for yourself, your team, or organization
                    </Text>
                  )}
                </FormControl>

                <Box
                  p={3}
                  bg={colorMode === "dark" ? "#0c0a09" : "white"}
                  borderRadius="lg"
                  borderWidth="1px"
                  borderColor={colorMode === "dark" ? "stone.700" : "stone.200"}
                >
                  <VStack spacing={2} align="stretch">
                    <HStack spacing={2}>
                      <Icon as={Clock} boxSize={4} color="orange.400" />
                      <Text fontSize="sm" fontWeight="medium">
                        Anonymous accounts expire after 90 days
                      </Text>
                    </HStack>
                    <Text fontSize="xs" color="stone.500">
                      Link with a real account to make it permanent (coming
                      soon).
                    </Text>
                  </VStack>
                </Box>

                <FormControl isInvalid={termsError}>
                  <Flex align="flex-start" gap={3}>
                    <Checkbox
                      isChecked={acceptedTerms}
                      onChange={(e) => {
                        setAcceptedTerms(e.target.checked);
                        setTermsError(false);
                      }}
                      colorScheme="brand"
                      sx={{
                        "& .chakra-checkbox__control": {
                          width: "18px",
                          height: "18px",
                          marginTop: "2px",
                        },
                      }}
                    />
                    <Box flex="1">
                      <Text
                        as="label"
                        fontSize="sm"
                        lineHeight="1.6"
                        display="block"
                      >
                        I have read and agree to the{" "}
                        <Link
                          as={RouterLink}
                          to="/terms"
                          color="brand.500"
                          textDecoration="underline"
                          _hover={{ color: "brand.600" }}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e: React.MouseEvent) => e.stopPropagation()}
                        >
                          Terms of Service
                        </Link>{" "}
                        and{" "}
                        <Link
                          as={RouterLink}
                          to="/privacy"
                          color="brand.500"
                          textDecoration="underline"
                          _hover={{ color: "brand.600" }}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e: React.MouseEvent) => e.stopPropagation()}
                        >
                          Privacy Policy
                        </Link>
                      </Text>
                    </Box>
                  </Flex>
                  {termsError && (
                    <FormErrorMessage fontSize="xs" ml="27px" mt={1}>
                      You must accept the Terms of Service to create an account
                    </FormErrorMessage>
                  )}
                </FormControl>
              </VStack>
            </ModalBody>
            <ModalFooter
              gap={3}
              pt={4}
              pb={6}
              borderTopWidth="1px"
              borderColor={colorMode === "dark" ? "stone.700" : "stone.100"}
            >
              <Button
                variant="ghost"
                onClick={onCancel}
                size="lg"
                flex={1}
              >
                Cancel
              </Button>
              <Button
                colorScheme="brand"
                onClick={onSignUp}
                isLoading={isSigningUp}
                isDisabled={!signUpUsername.trim()}
                size="lg"
                flex={1}
              >
                Create Account
              </Button>
            </ModalFooter>
          </>
        ) : (
          <>
            <ModalHeader
              pb={4}
              pt={6}
              borderBottomWidth="1px"
              borderColor={colorMode === "dark" ? "stone.700" : "stone.100"}
            >
              <VStack spacing={1} align="start">
                <Text fontSize="xl" fontWeight="bold">
                  Your API Key
                </Text>
                <Text fontSize="sm" fontWeight="normal" color="stone.500">
                  Save this key securely. You won't see it again after logging
                  out
                </Text>
              </VStack>
            </ModalHeader>
            <ModalBody pt={6} pb={6}>
              <Box
                bg={colorMode === "dark" ? "stone.900" : "stone.50"}
                borderRadius="lg"
                p={4}
                borderWidth="1px"
                borderColor={colorMode === "dark" ? "stone.700" : "stone.200"}
              >
                <VStack spacing={3} align="stretch">
                  <Text
                    fontFamily="mono"
                    fontSize="sm"
                    wordBreak="break-all"
                    color={colorMode === "dark" ? "stone.100" : "stone.800"}
                  >
                    {generatedKey}
                  </Text>
                  <Button
                    onClick={onCopy}
                    leftIcon={
                      <Icon as={hasCopied ? Check : Copy} boxSize={4} />
                    }
                    colorScheme={hasCopied ? "green" : "brand"}
                    variant={hasCopied ? "solid" : "outline"}
                    size="sm"
                    w="full"
                  >
                    {hasCopied
                      ? "Copied to Clipboard!"
                      : "Copy to Clipboard"}
                  </Button>
                </VStack>
              </Box>
            </ModalBody>
            <ModalFooter
              pt={4}
              pb={6}
              borderTopWidth="1px"
              borderColor={colorMode === "dark" ? "stone.700" : "stone.100"}
            >
              <Button
                colorScheme="brand"
                onClick={onComplete}
                w="full"
                size="lg"
              >
                Continue
              </Button>
            </ModalFooter>
          </>
        )}
      </ModalContent>
    </Modal>
  );
}
